import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import ScoreBadge from "../components/ScoreBadge";
import { Button } from "../components/ui/button";
import { Play, Pause, X, Zap, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["active", "paused", "completed", "stopped", "all"];

const STEP_LABELS = {
  insight: "Insight",
  case_study: "Case Study",
  trigger: "Trigger Event",
  meeting_ask: "Meeting Ask",
  follow_up: "Follow-up",
  breakup: "Breakup",
  nurture: "Nurture",
};

const STEPS_ORDER = ["insight", "case_study", "trigger", "meeting_ask", "follow_up", "follow_up", "breakup"];

function formatNext(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hrs = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (diffMs < 0) return "Due now";
  if (days > 0) return `in ${days}d ${hrs}h`;
  if (hrs > 0) return `in ${hrs}h`;
  const mins = Math.floor(diffMs / (1000 * 60));
  return `in ${mins}m`;
}

export default function Enrollments() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [autoSend, setAutoSend] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [enrRes, sRes] = await Promise.all([
        api.get("/enrollments", { params: { status: statusFilter } }),
        api.get("/settings"),
      ]);
      setItems(enrRes.data);
      setAutoSend(!!sRes.data.auto_send);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const update = async (id, status) => {
    try {
      await api.patch(`/enrollments/${id}`, { status });
      toast.success(status);
      load();
    } catch { toast.error("Failed"); }
  };

  const stop = async (id) => {
    try {
      await api.delete(`/enrollments/${id}`);
      toast.success("Stopped");
      load();
    } catch { toast.error("Failed"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/enrollments/run-now");
      toast.success(`Scheduler tick — ${data.processed} enrollment(s) advanced`);
      load();
    } catch {
      toast.error("Run failed");
    } finally {
      setRunning(false);
    }
  };

  const toggleAutoSend = async () => {
    try {
      const { data } = await api.patch("/settings", { auto_send: !autoSend });
      setAutoSend(!!data.auto_send);
      toast.success(data.auto_send ? "Auto-send ON — scheduler will send emails" : "Auto-send OFF — scheduler will only draft");
    } catch { toast.error("Failed to update"); }
  };

  return (
    <Layout
      title="Enrollments"
      subtitle="Auto-advance"
      actions={
        <>
          <Button variant="outline" onClick={toggleAutoSend} data-testid="toggle-auto-send" style={{ borderRadius: 2 }}>
            <Zap className="h-4 w-4 mr-1" strokeWidth={1.5} /> Auto-send: {autoSend ? "ON" : "OFF"}
          </Button>
          <Button onClick={runNow} disabled={running} data-testid="run-now-btn" style={{ borderRadius: 2 }}>
            <Clock className="h-4 w-4 mr-1" strokeWidth={1.5} /> {running ? "Running…" : "Run scheduler now"}
          </Button>
        </>
      }
    >
      <div className="bg-card border border-border p-5 mb-6" style={{ borderRadius: 2 }}>
        <div className="flex items-start gap-4">
          <Clock className="h-5 w-5 text-primary mt-1" strokeWidth={1.5} />
          <div>
            <div className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Scheduler runs every 60 seconds</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              The cron worker checks all <strong className="text-foreground">active</strong> enrollments and advances any whose next-send time has passed. It auto-pauses enrollments when a lead replies, books a meeting, or moves to nurture. With <strong className="text-foreground">auto-send {autoSend ? "ON" : "OFF"}</strong>, the worker will{" "}
              {autoSend ? "send each step's AI-drafted email automatically (respecting your daily cap)." : "queue each step's email as a draft for your review in the lead drawer."}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-card border border-border p-1 mb-4 w-fit" style={{ borderRadius: 2 }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`enr-filter-${s}`}
            className={`px-3 py-1.5 text-xs capitalize ${statusFilter === s ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
            style={{ borderRadius: 2 }}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border" style={{ borderRadius: 2 }}>
        <div className="px-6 py-3 grid grid-cols-12 gap-4 border-b border-border">
          <div className="col-span-3 overline">Lead</div>
          <div className="col-span-2 overline">Company</div>
          <div className="col-span-2 overline">Step</div>
          <div className="col-span-2 overline">Next send</div>
          <div className="col-span-1 overline">Status</div>
          <div className="col-span-2 overline text-right">Actions</div>
        </div>

        {loading && <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            No enrollments here. Open a lead → "Sequence" tab → Enroll.
          </div>
        )}

        {!loading && items.map((e) => {
          const stepKind = STEPS_ORDER[e.current_step] || "completed";
          return (
            <div key={e.enrollment_id} className="tbl-row px-6 py-4 grid grid-cols-12 gap-4 items-center" data-testid={`enr-${e.enrollment_id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-medium truncate">{e.lead?.full_name || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{e.lead?.email}</div>
              </div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{e.lead?.company || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{e.lead?.industry}</div>
              </div>
              <div className="col-span-2">
                <div className="text-sm">{e.current_step + 1}/7 · {STEP_LABELS[stepKind] || "Done"}</div>
              </div>
              <div className="col-span-2 font-mono text-xs">{e.status === "active" ? formatNext(e.next_send_at) : "—"}</div>
              <div className="col-span-1 text-xs capitalize">{e.status}</div>
              <div className="col-span-2 flex justify-end gap-1">
                {e.status === "active" && (
                  <Button size="sm" variant="outline" onClick={() => update(e.enrollment_id, "paused")} data-testid={`pause-${e.enrollment_id}`} style={{ borderRadius: 2 }}>
                    <Pause className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                )}
                {e.status === "paused" && (
                  <Button size="sm" variant="outline" onClick={() => update(e.enrollment_id, "active")} data-testid={`resume-${e.enrollment_id}`} style={{ borderRadius: 2 }}>
                    <Play className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                )}
                {(e.status === "active" || e.status === "paused") && (
                  <Button size="sm" variant="outline" onClick={() => stop(e.enrollment_id)} data-testid={`stop-${e.enrollment_id}`} style={{ borderRadius: 2 }}>
                    <X className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
